import type { NavigateFunction } from 'react-router';

import log from 'loglevel';

const EXTERNAL_WINDOW_TARGET = '_blank';
const EXTERNAL_WINDOW_FEATURES = 'noopener,noreferrer';

const PROTOCOL_RELATIVE_PREFIX = '//';
const ROUTABLE_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:']);

/**
 * Absolute means "has a scheme, OR is protocol-relative". `//host/path` is the one that
 * looks relative and is not: parsing it without a base throws, and treating it as relative
 * hands a cross-origin target to `navigate`, which pushState rejects and history v5 then
 * resolves by a full-page `location.assign` — off-origin, with no `noopener`.
 *
 * Everything genuinely relative goes back untouched, because `navigate` resolves it against
 * the current route and that is what its callers rely on: flattening `?tab=members` to a
 * path throws the user to the app root.
 *
 * A same-origin ABSOLUTE url is still stripped to a path — `navigate` has no absolute-URL
 * handling (only `<Link>`'s parser does), so the raw string resolves against the current
 * route and lands on a blank SPA path. That is the bug this file exists to fix, and the
 * first version of it reproduced the bug for our own origin.
 */
const resolveRedirect = (
  redirectUrl: string,
): { isExternal: boolean; protocol: string; target: string } => {
  const isProtocolRelative = redirectUrl.startsWith(PROTOCOL_RELATIVE_PREFIX);
  let url: URL;

  try {
    url = isProtocolRelative
      ? new URL(redirectUrl, window.location.origin)
      : new URL(redirectUrl);
  } catch {
    return {
      isExternal: false,
      protocol: window.location.protocol,
      target: redirectUrl,
    };
  }

  return {
    isExternal: url.origin !== window.location.origin,
    protocol: url.protocol,
    target: `${url.pathname}${url.search}${url.hash}`,
  };
};

/**
 * react-router's `navigate` has no absolute-URL handling — only `<Link>`'s own parser does —
 * so a string that does not start with `/` is resolved against the CURRENT route. Handing it
 * `https://myaccount.google.com/permissions` lands the user on
 * `/integration/https:/myaccount.google.com/permissions`, a blank SPA route, with no error.
 *
 * Every notification `redirectUrl` was an in-app path until one pointed at a provider, which
 * is why this only had to exist now.
 */
export const openNotificationRedirect = (
  redirectUrl: string,
  navigate: NavigateFunction,
): void => {
  const { isExternal, protocol, target } = resolveRedirect(redirectUrl);

  if (isExternal) {
    // `javascript:` and `data:` parse cleanly and report an origin of "null", so an origin
    // comparison alone sends them to window.open. Server-written today, but this is now the
    // single chokepoint every notification redirect passes through.
    if (!ROUTABLE_PROTOCOLS.has(protocol)) {
      log.error(
        'Refusing a notification redirect with an unroutable protocol',
        {
          protocol,
        },
      );
      return;
    }

    window.open(redirectUrl, EXTERNAL_WINDOW_TARGET, EXTERNAL_WINDOW_FEATURES);
    return;
  }

  navigate(target);
};
